import React from 'react';
import { createRoot } from 'react-dom/client';

import { observer } from 'mobx-react';
import { Popconfirm, PopconfirmProps } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useDefaultZIndex } from 'framework/react-integration';
import { CssVariables } from 'styles/cssVariables';

export interface AntPopconfirmProps extends Omit<
	PopconfirmProps,
	'overlayClassName' | 'overlayStyle' | 'overlayInnerStyle' | 'destroyTooltipOnHide'
> {
	type?: 'question' | 'message';
}

export const AntPopconfirm = observer((props: AntPopconfirmProps) => {
	const { styles, ...rest } = props;

	const getIcon = React.useCallback(() => {
		switch (props.type) {
			case 'question':
				return <QuestionCircleOutlined style={{ color: 'red' }} />;
			default:
				return undefined;
		}
	}, [props.type]);

	// styles can also be a function (per-render semantic customization) - not used anywhere in
	// our codebase so just ignored for now
	const stylesObj = typeof styles === 'function' ? undefined : styles;
	const rootStyleWrapped = useDefaultZIndex(stylesObj?.root, CssVariables.zIndexControlPopover);

	return <Popconfirm {...rest} styles={{ ...stylesObj, root: rootStyleWrapped }} icon={getIcon()} />;
});

export type OpenPopConfirmArgs = AntPopconfirmProps & {
	top: number;
	left: number;
};

export function openPopConfirm(args: Omit<OpenPopConfirmArgs, 'open'>): Promise<boolean> {
	const { top, left } = args;
	return new Promise<boolean>((resolve) => {
		const { onOpenChange, onCancel, onConfirm, ...rest } = args;

		const container = document.createElement('div');

		document.body.append(container);
		container.style.position = 'absolute';
		container.style.left = left - 50 + 'px';
		container.style.top = top + 'px';
		container.style.width = '100px';

		const root = createRoot(container);

		const onOpenChangedWrapper = (visible: boolean) => {
			onOpenChange?.(visible);
			root.unmount();
		};

		const onConfirmWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onConfirm?.(e);
			resolve(true);
		};

		const onCancelWrapper = (e?: React.MouseEvent<HTMLElement>) => {
			onCancel?.(e);
			resolve(false);
		};

		root.render(
			<AntPopconfirm
				open={true}
				onOpenChange={onOpenChangedWrapper}
				onConfirm={onConfirmWrapper}
				onCancel={onCancelWrapper}
				{...rest}
			>
				<div></div>
			</AntPopconfirm>,
		);
	});
}
